
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import GalleryNav from '../../components/feature/GalleryNav';
import CategoryFilter from '../../components/feature/CategoryFilter';
import GalleryGrid from '../../components/feature/GalleryGrid';
import Lightbox from '../../components/feature/Lightbox';
import { honeybeeCategories, honeybeeImages } from '../../mocks/honeybeeGallery';

export default function HoneybeeGalleryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filteredImages = useMemo(() => {
    if (activeCategory === 'All') return honeybeeImages;
    return honeybeeImages.filter((img) => img.category === activeCategory);
  }, [activeCategory]);

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <GalleryNav />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7f067113b3811c24b7d00967f8a8e498/d7aa89527c8c033110903407ff21e028.jpeg"
            alt="The Honey Bee living room with Bee Kind chairs"
            title="The Honey Bee - Cozy Cottage Vacation Rental"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium mb-4 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            <span>Back to Home</span>
          </Link>
          <span className="inline-block px-4 py-1.5 bg-amber-500/20 backdrop-blur-sm text-amber-200 rounded-full text-xs font-medium mb-4">Bee-Themed Cottage</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">The Honey Bee</h1>
          <p className="text-base text-white/80 max-w-2xl leading-relaxed">Experience the charm of our bee-themed cottage in Door County! Our cozy retreat is the perfect blend of comfort and modern, while being close to the buzzing Sturgeon Bay shops.</p>
          <div className="flex items-center gap-6 mt-5">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-user-line"></i></div>
              <span>Sleeps 4</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-hotel-bed-line"></i></div>
              <span>2 Bedrooms</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-drop-line"></i></div>
              <span>1 Bathroom</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-image-line"></i></div>
              <span>{honeybeeImages.length} Photos</span>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <a href="https://www.airbnb.com/multicalendar/1131825734779758286" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#FF5A5F] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#e04e52] transition-all cursor-pointer whitespace-nowrap shadow-lg">
              <i className="ri-home-wifi-line"></i>
              <span>Book on Airbnb</span>
            </a>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-16 bg-amber-50/30">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full">
                <i className="ri-home-heart-line text-amber-600 text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">About This Property</h2>
            </div>
            <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed space-y-4">
              <p>You'll be greeted by the inviting warmth of our living space. Sink into plush furniture or relax in one of our 2 thoughtfully decorated bedrooms, each featuring a king bed and smart TV. Romantic getaway or a fun-filled family vacation, our bee-themed cottage offers a unique retreat.</p>
              <p className="font-semibold text-gray-900">Book your stay with us today and find out what the buzz is all about!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Guest Review Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-8 md:p-10 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-sm">
                <i className="ri-star-fill text-amber-500 text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Guest Experience</h2>
                <p className="text-sm text-amber-600 font-semibold mt-1">Overall Rating: 5.0</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-1 mb-4">
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                </div>
                <p className="text-gray-700 text-base leading-relaxed mb-4">"Michelle's home was the perfect spot for us and our 15 month old. It is an easy and nice walk to town with lots of shops and restaurants. The house is so cute with lots of open space for our little to play with his toys. House is very bright and comfortable! Would recommend!!"</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">— Kellsie</p>
                  <p className="text-sm text-gray-500">February 2026</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-1 mb-4">
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                </div>
                <p className="text-gray-700 text-base leading-relaxed mb-4">"We enjoyed staying at Michelle's place, it was great! perfect location and very clean. Thank you so much for having us!!!"</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-900">— Amy</p>
                  <p className="text-sm text-gray-500">September 2025</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Photo Gallery</h2>
              <p className="text-sm text-gray-500">{filteredImages.length} photos{activeCategory !== 'All' ? ` in ${activeCategory}` : ''}</p>
            </div>
            <CategoryFilter
              categories={honeybeeCategories}
              activeCategory={activeCategory}
              onSelect={setActiveCategory}
            />
          </div>

          <GalleryGrid images={filteredImages} onImageClick={handleImageClick} />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready for a Cozy Getaway?</h3>
          <p className="text-sm text-gray-600 mb-8 leading-relaxed">Book The Honey Bee for your next relaxing cottage escape. Starting from $349/night.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="https://www.airbnb.com/multicalendar/1131825734779758286" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#FF5A5F] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#e04e52] transition-all cursor-pointer whitespace-nowrap shadow-lg">
              <i className="ri-home-wifi-line"></i>
              <span>Book on Airbnb</span>
            </a>
            <Link to="/gallery/the-olivian-on-4th" className="border-2 border-gray-900 text-gray-900 px-8 py-3 rounded-full text-sm font-semibold hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap">
              View The Olivian on 4th
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-50 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; 2024 Short Term Rentals. All rights reserved.</p>
          <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer">Powered by Readdy</a>
        </div>
      </footer>

      <Lightbox
        images={filteredImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
}
